import { PractitionerActivity, ActivityType } from './activityApi';

/**
 * Get the effective color for an activity
 * Uses colorOverride if set, otherwise falls back to activityType defaultColor
 */
export function getActivityColor(activity: PractitionerActivity, activityType?: ActivityType): string {
  if (activity.colorOverride) {
    return activity.colorOverride;
  }
  if (activity.effectiveColor) {
    return activity.effectiveColor;
  }
  if (activityType?.defaultColor) {
    return activityType.defaultColor;
  }
  return '#3B82F6'; // Default blue
}

/**
 * Get the effective blocking status for an activity
 */
export function getActivityIsBlocking(activity: PractitionerActivity, activityType?: ActivityType): boolean {
  if (activity.effectiveIsBlocking !== undefined) {
    return activity.effectiveIsBlocking;
  }
  if (activity.isBlockingOverride !== null && activity.isBlockingOverride !== undefined) {
    return activity.isBlockingOverride;
  }
  if (activityType?.isBlocking !== undefined) {
    return activityType.isBlocking;
  }
  return true; // Default to blocking
}

/**
 * Format activity date-time for display
 */
export function formatActivityDateTime(dateTime: string, timezone: string = 'Europe/Paris'): string {
  try {
    const date = new Date(dateTime);
    return date.toLocaleString('fr-FR', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateTime;
  }
}

/**
 * Format activity duration
 */
export function formatActivityDuration(start: string, end: string): string {
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h${diffMinutes > 0 ? ` ${diffMinutes}min` : ''}`;
    }
    return `${diffMinutes}min`;
  } catch (error) {
    return '';
  }
}

/**
 * Get activity status badge color
 */
export function getActivityStatusColor(status: PractitionerActivity['status']): string {
  switch (status) {
    case 'PLANNED':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'CANCELLED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Get activity visibility label
 */
export function getActivityVisibilityLabel(visibility: PractitionerActivity['visibility']): string {
  switch (visibility) {
    case 'INTERNAL':
      return 'Interne';
    case 'FRONTOFFICE':
      return 'Public';
    case 'PRIVATE':
      return 'Privé';
    default:
      return visibility;
  }
}

/**
 * Validate activity date range
 */
export function validateActivityDates(startDateTime: string, endDateTime: string): { valid: boolean; error?: string } {
  try {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);

    if (isNaN(start.getTime())) {
      return { valid: false, error: 'Date de début invalide' };
    }

    if (isNaN(end.getTime())) {
      return { valid: false, error: 'Date de fin invalide' };
    }

    if (end <= start) {
      return { valid: false, error: 'La date de fin doit être après la date de début' };
    }

    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Erreur de validation des dates' };
  }
}

/**
 * Check if activity overlaps with another activity
 */
export function activitiesOverlap(
  activity1: { startDateTime: string; endDateTime: string },
  activity2: { startDateTime: string; endDateTime: string }
): boolean {
  const start1 = new Date(activity1.startDateTime);
  const end1 = new Date(activity1.endDateTime);
  const start2 = new Date(activity2.startDateTime);
  const end2 = new Date(activity2.endDateTime);

  return start1 < end2 && start2 < end1;
}

/**
 * Filter activities by date range
 */
export function filterActivitiesByDateRange(
  activities: PractitionerActivity[],
  from: Date,
  to: Date
): PractitionerActivity[] {
  return activities.filter(activity => {
    const activityStart = new Date(activity.startDateTime);
    const activityEnd = new Date(activity.endDateTime);
    return activityStart < to && activityEnd > from;
  });
}

/**
 * Group activities by practitioner
 */
export function groupActivitiesByPractitioner(
  activities: PractitionerActivity[]
): Map<string, PractitionerActivity[]> {
  const grouped = new Map<string, PractitionerActivity[]>();

  activities.forEach(activity => {
    const key = activity.practitionerId || activity.employeeId || 'unassigned';
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(activity);
  });

  return grouped;
}

/**
 * Group activities by activity type
 */
export function groupActivitiesByType(
  activities: PractitionerActivity[]
): Map<string, PractitionerActivity[]> {
  const grouped = new Map<string, PractitionerActivity[]>();

  activities.forEach(activity => {
    const key = activity.activityTypeId ?? 'untyped';
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(activity);
  });

  return grouped;
}

